/**
 * GovServicesMonthlyPage — `/reports/gov-services-monthly`.
 *
 * Wired to GET /v1/reports/gov-services-monthly?month=YYYY-MM on
 * service-reporting-api. Uses SourceLoaderService.loadGovernmentRequests.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';

import { getGovServicesMonthlyReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

function thisMonthIso(): string {
  return new Date().toISOString().slice(0, 7);
}

export function GovServicesMonthlyPage(): JSX.Element {
  const report = findPreBuiltReport('gov-services-monthly');
  const [month, setMonth] = useState<string>(thisMonthIso());

  const query = useQuery({
    queryKey: ['pre-built', 'gov-services-monthly', month],
    queryFn: () => getGovServicesMonthlyReport(month),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  return (
    <ReportShell report={report}>
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Month</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month"
              className="px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="MDAs served" value={data ? data.rows.length.toLocaleString('en-US') : '—'} hint="Government services" icon={Building2} />
        <KPICard title="Total requests" value={data ? data.totalRequests.toLocaleString('en-US') : '—'} hint="Government services" />
        <KPICard title="Completed" value={data ? data.completedRequests.toLocaleString('en-US') : '—'} hint="approved + completed" tone="success" />
        <KPICard title="Month" value={data?.month ?? '—'} hint="YYYY-MM" />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readGovServicesMonthly</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">MDA breakdown — {data.month}</h3>
            {data.stub && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Preview data — full data coming soon
              </span>
            )}
          </div>
          {data.rows.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No government service requests found for {data.month}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Agency</th>
                    <th className="text-right px-4 py-2">Submitted</th>
                    <th className="text-right px-4 py-2">Completed</th>
                    <th className="text-right px-4 py-2">Rejected</th>
                    <th className="text-right px-4 py-2">Completion %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((row) => (
                    <tr key={row.agency}>
                      <td className="px-4 py-2 font-mono text-xs">{row.agency}</td>
                      <td className="px-4 py-2 text-right text-xs">{row.submitted.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs text-nipost-green">{row.completed.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs text-red-600">{row.rejected.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs font-semibold">{row.completionPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReportShell>
  );
}
